import { createMachine, assign, send, DoneInvokeEvent } from 'xstate';
import { DataAccess } from '@chargify/data-access';

import {
  setListExpandedState,
  toggleSubscriptionDetailsVisibility,
  updateSubscription,
} from './actions';
import { serializeSubscriptions, serializeCatalogs } from './serializers';

import { Context, Events } from './types';

const createSubscriptionsMachine = (dataAccess: DataAccess) =>
  createMachine<Context, Events>(
    {
      id: 'subscriptions',
      predictableActionArguments: true,
      initial: 'idle',
      context: {
        items: {},
        catalogs: {},
        itemsWithVisibleDetails: [],
      },
      on: {
        UPDATE_SUBSCRIPTION_COMPONENTS: {
          actions: [
            send(
              { type: 'UPDATE_COMPONENTS' },
              {
                to: (_ctx, event) =>
                  `subscription-components-${event.subscriptionId}`,
              }
            ),
          ],
        },
      },
      states: {
        idle: {
          on: {
            FETCH_SUBSCRIPTIONS: {
              target: 'loading',
            },
          },
        },
        loading: {
          invoke: {
            id: 'subscriptionsFetch',
            src: 'getSubscriptions',
            onDone: {
              target: 'ready',
              actions: assign(
                (
                  _ctx,
                  event: DoneInvokeEvent<{
                    items: Record<string, any>[];
                  }>
                ) => {
                  const subscriptions = serializeSubscriptions(
                    event.data.items,
                    dataAccess
                  );

                  return {
                    items: subscriptions,
                    catalogs: serializeCatalogs(subscriptions),
                  };
                }
              ),
            },
            onError: {
              target: 'error',
            },
          },
        },
        error: {
          on: {
            RETRY_FETCH_SUBSCRIPTIONS: {
              target: 'loading',
            },
          },
        },
        ready: {
          on: {
            TOGGLE_SUBSCRIPTION_DETAILS_VISIBILITY: {
              actions: 'toggleSubscriptionDetailsVisibility',
            },
            SET_LIST_EXPANDED: {
              actions: 'setListExpandedState',
            },
            UPDATE_SUBSCRIPTION: {
              actions: [
                updateSubscription,
                send(
                  (_ctx, event) => ({
                    type: 'UPDATE_SUBSCRIPTION',
                    data: event,
                  }),
                  {
                    to: (_ctx, event) => `subscription-${event.subscriptionId}`,
                  }
                ),
              ],
            },
          },
        },
      },
    },
    {
      actions: {
        updateSubscription,
        toggleSubscriptionDetailsVisibility,
        setListExpandedState,
      },
    }
  );

export { createSubscriptionsMachine };
